/**
 * Reducer-only client store (M8-B).
 *
 * Contract (frozen seam):
 *  - `ingest(msg)` folds an authoritative `ServerMsg` into `ClientState`. The
 *    client runs NO card hooks and never mutates a `GameState` (reducer-only,
 *    D22/R9). In iteration 1 the server always sends a full per-player redacted
 *    `snapshot` (D30, tech/24 §5.1), so the store simply adopts it; `events`
 *    only feed the derived log for `<EventLog>` (R14 — the log is NOT part of
 *    `GameState`), never the snapshot.
 *  - `send(action)` / `resync()` turn UI intents into outgoing `ClientMsg`.
 *  - `subscribe`/`getState` are `useSyncExternalStore`-compatible: `getState`
 *    returns a stable reference between changes and a fresh one on every change
 *    (otherwise React would loop forever).
 */
import { PROTOCOL_VERSION, parseSavedDeckSelectionRef } from '../../server/protocol.js';
import type { ClientMsg, SavedDeckSelectionRef, ServerMsg } from '../../server/protocol.js';
import type { ActionSink, ClientState, ClientStatus, LogEntry } from '../types.js';

export interface ClientStore {
  getState(): ClientState;
  subscribe(listener: () => void): () => void;
  /** Fold an authoritative server message into client state. */
  ingest(msg: ServerMsg): void;
  /** Emit a player action (PlanCard / UnplanCard / Ready / Resolve) to the server. */
  send: ActionSink;
  /** Select one available prepared deck while the room is choosing decks. */
  selectDeck(deckDefinitionId: string): void;
  /** Select one account-owned saved-deck revision; composition never crosses this boundary. */
  selectSavedDeck(deck: SavedDeckSelectionRef): void;
  /** Lock the viewer's current deck selection. */
  lockDeck(): void;
  /** Request a fresh authoritative snapshot. */
  resync(): void;
  /** Transport reported an error (parse/socket) — surface it, don't freeze the UI. */
  notifyError(message: string): void;
  /** Transport closed — a close before the match ended is an error, not a clean exit. */
  notifyClosed(code: number, reason: string): void;
  /** Transport is retrying; keep the authoritative board instead of showing a terminal error. */
  notifyReconnecting(code: number, reason: string, delayMs: number): void;
}

export interface ClientStoreOptions {
  /** Sink for outgoing frames — provided by the transport. */
  send: (msg: ClientMsg) => void;
}

/** Keep the derived log bounded so a long match cannot grow it without limit (R14). */
const MAX_LOG_ENTRIES = 500;

const INITIAL: ClientState = { status: 'connecting', seq: -1, log: [] };

export function createClientStore(opts: ClientStoreOptions): ClientStore {
  // One immutable ClientState; its identity changes on every update and is
  // otherwise stable (useSyncExternalStore requirement).
  let state: ClientState = INITIAL;
  const listeners = new Set<() => void>();
  // Monotonic counters — plain client-side bookkeeping, not game state.
  let clientSeq = 0;
  let nextLogIndex = 0;

  function commit(next: ClientState): void {
    // Always a fresh object from a spread, so identity already differs; the
    // guard just documents the "no change ⇒ no notify" contract.
    if (next === state) return;
    state = next;
    for (const listener of listeners) listener();
  }

  function rejectUpdatedServer(
    reason = 'Версия сервера изменилась. Эту комнату больше нельзя продолжить.',
    terminal = false,
  ): void {
    const next: ClientState = {
      ...state,
      status: 'error',
      lastError: reason,
      serverUpdated: true,
      lastRejectedClientSeq: -1,
      ...(terminal ? { terminalCredentialRejection: 'SERVER_UPDATED' as const } : {}),
    };
    delete next.reconnectToken;
    commit(next);
  }

  function rejectInvalidSession(reason: string): void {
    const next: ClientState = {
      ...state,
      status: 'error',
      lastError: reason,
      invalidSession: true,
      lastRejectedClientSeq: -1,
      terminalCredentialRejection: 'INVALID_SESSION',
    };
    delete next.reconnectToken;
    commit(next);
  }

  function ingest(msg: ServerMsg): void {
    switch (msg.t) {
      case 'session':
        if (msg.protocolVersion !== PROTOCOL_VERSION) {
          rejectUpdatedServer();
          return;
        }
        // A reconnect can bind a fresh store to a server session that already
        // consumed pre-game/game commands. Resume the authoritative sequence
        // instead of replaying clientSeq from zero after a page reload.
        // A repeated session invalidates the mounted playback queue by forcing
        // ActiveGame to unmount. Keep the useful historical log: the hook that
        // mounts after the fresh snapshot starts from its authoritative seq and
        // therefore cannot enqueue those older entries again.
        const repeatedSession = state.reconnectToken !== undefined;
        clientSeq = msg.nextClientSeq;
        // Learn our seat authoritatively; stay 'connecting' until first snapshot.
        const acknowledged = {
          ...state,
          roomId: msg.roomId,
          viewerId: msg.playerId,
          reconnectToken: msg.reconnectToken,
          sessionAckCount: (state.sessionAckCount ?? 0) + 1,
          ...(repeatedSession ? { status: 'connecting' as const } : {}),
        };
        delete acknowledged.lastError;
        delete acknowledged.lastRejectedClientSeq;
        delete acknowledged.serverUpdated;
        delete acknowledged.invalidSession;
        delete acknowledged.terminalCredentialRejection;
        if (state.roomId !== undefined && state.roomId !== msg.roomId) {
          delete acknowledged.replayDecks;
        }
        commit(acknowledged);
        return;

      case 'room_state': {
        if (msg.protocolVersion !== PROTOCOL_VERSION) {
          rejectUpdatedServer();
          return;
        }
        const next: ClientState = {
          ...state,
          roomState: msg,
          status: 'active',
        };
        delete next.lastError;
        delete next.lastRejectedClientSeq;
        commit(next);
        return;
      }

      case 'snapshot': {
        // The snapshot is the authoritative full redacted state (D30); we adopt
        // it wholesale and never derive state from events in iteration 1.
        const status: ClientStatus = msg.state.status.phase === 'ended' ? 'ended' : 'active';
        const next: ClientState = { ...state, snapshot: msg.state, seq: msg.seq, status };
        // Reconcile the choice prompt: a resolved choice disappears from the
        // snapshot, so clear any stale pendingChoice.
        if (msg.state.pendingChoice === undefined && next.pendingChoice !== undefined) {
          delete next.pendingChoice; // fresh object from the spread — safe to trim.
        }
        if (next.replayDecks !== undefined
          && (status !== 'ended' || next.replayDecks.seq !== msg.seq)) {
          delete next.replayDecks;
        }
        commit(next);
        return;
      }

      case 'replay_decks': {
        const ended = state.snapshot?.status.phase === 'ended';
        const matchesRoom = state.roomId === msg.roomId;
        if (!ended || !matchesRoom || state.seq !== msg.seq) {
          commit({
            ...state,
            lastError: 'Составы реплея получены до соответствующего завершённого snapshot.',
          });
          return;
        }
        const next = { ...state, replayDecks: msg };
        delete next.lastError;
        commit(next);
        return;
      }

      case 'events': {
        // Append informational events to the derived log (R14); never touch the
        // snapshot from events.
        const log: LogEntry[] = state.log.slice();
        for (const event of msg.events) {
          log.push({ index: nextLogIndex, seq: msg.seq, event });
          nextLogIndex += 1;
        }
        const capped = log.length > MAX_LOG_ENTRIES ? log.slice(log.length - MAX_LOG_ENTRIES) : log;
        commit({
          ...state,
          log: capped,
          ...(msg.ackClientSeq === undefined ? {} : { lastAckedClientSeq: msg.ackClientSeq }),
        });
        return;
      }

      case 'log_history': {
        nextLogIndex = 0;
        const log = msg.entries.slice(-MAX_LOG_ENTRIES).map(({ seq, event }) => ({
          index: nextLogIndex++,
          seq,
          event,
        }));
        commit({ ...state, log });
        return;
      }

      case 'rejected':
        if (msg.code === 'SERVER_UPDATED' || msg.code === 'PROTOCOL_MISMATCH') {
          rejectUpdatedServer(msg.reason, msg.code === 'SERVER_UPDATED');
          return;
        }
        if (msg.code === 'INVALID_SESSION') {
          rejectInvalidSession(msg.reason);
          return;
        }
        // Recoverable: surface the reason, leave snapshot/status untouched.
        if (msg.clientSeq === -1) {
          const rejectedState = { ...state, lastError: msg.reason, lastRejectedClientSeq: msg.clientSeq };
          commit(rejectedState);
        } else {
          commit({ ...state, lastError: msg.reason, lastRejectedClientSeq: msg.clientSeq });
        }
        return;

      case 'choose':
        commit({ ...state, pendingChoice: msg.choice });
        return;

      case 'pong':
        // No-op — identity stays stable so useSyncExternalStore doesn't re-render.
        return;
    }
  }

  const send: ActionSink = (action) => {
    // Single outgoing path for PlanCard / UnplanCard / Ready AND Resolve.
    opts.send({ t: 'action', action, clientSeq });
    clientSeq += 1;
  };

  return {
    getState() {
      return state;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    ingest,
    send,
    selectDeck(deckDefinitionId) {
      opts.send({ t: 'select_deck', deckDefinitionId, clientSeq });
      clientSeq += 1;
    },
    selectSavedDeck(deck) {
      opts.send({ t: 'select_deck', deck: parseSavedDeckSelectionRef(deck), clientSeq });
      clientSeq += 1;
    },
    lockDeck() {
      opts.send({ t: 'lock_deck', clientSeq });
      clientSeq += 1;
    },
    resync() {
      opts.send({ t: 'resync' });
    },
    notifyError(message) {
      commit({ ...state, status: 'error', lastError: message });
    },
    notifyReconnecting(code, reason, delayMs) {
      const cause = reason.length > 0
        ? `Соединение закрыто: ${reason} (код ${code}).`
        : code === 1006
          ? 'Связь с сервером неожиданно прервалась (код 1006).'
          : `Соединение закрыто (код ${code}).`;
      const delay = delayMs < 1_000
        ? `${delayMs} мс`
        : `${String(delayMs / 1_000).replace('.', ',')} с`;
      commit({
        ...state,
        status: 'connecting',
        lastError: `${cause} Следующая попытка через ${delay}.`,
      });
    },
    notifyClosed(code, reason) {
      // A close after the match is over is the expected clean teardown — stay 'ended'.
      if (state.status === 'ended') return;
      // The server sends a precise rejection frame immediately before policy
      // close 1008; do not hide it behind the generic close reason.
      if (code === 1008 && state.lastRejectedClientSeq === -1 && state.lastError !== undefined) {
        commit({ ...state, status: 'error' });
        return;
      }
      const detail = reason.length > 0 ? `соединение закрыто (${code}): ${reason}` : `соединение закрыто (${code})`;
      commit({ ...state, status: 'error', lastError: detail });
    },
  };
}
